<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[80] overflow-y-auto">
      <div class="fixed inset-0 bg-black/45" @click="close"></div>
      <div class="relative flex min-h-screen items-start justify-center p-3 sm:items-center sm:p-4">
        <div class="relative flex max-h-[calc(100vh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[88vh]">
          <header class="border-b border-slate-200 px-4 py-4 sm:px-5">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">下游标签偏好</h3>
                <p class="mt-1 text-sm text-slate-500">{{ companyName }} · 软评分规则，优先匹配但不保证命中。</p>
              </div>
              <button type="button" class="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" @click="close">
                <span class="sr-only">关闭</span>
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>
          </header>

          <main class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
            <div class="overflow-hidden rounded-lg border border-slate-200">
              <div ref="tagPreferenceTableScrollRef" class="overflow-x-auto">
                <table class="min-w-[640px] w-full divide-y divide-slate-200 text-sm">
                  <thead class="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                    <tr>
                      <th class="px-3 py-2.5">偏好标签</th>
                      <th class="w-24 px-3 py-2.5 text-center">启用</th>
                      <th class="w-28 px-3 py-2.5 text-right">优先级</th>
                      <th class="data-table-sticky-right data-table-sticky-head data-table-sticky-action w-16 px-3 py-2.5 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 bg-white">
                    <tr v-if="rows.length === 0">
                      <td colspan="4" class="px-3 py-8 text-center text-slate-500">暂无标签偏好</td>
                    </tr>
                    <tr v-for="(row, index) in rows" :key="row.localId">
                      <td class="px-3 py-2.5">
                        <el-select
                          v-model="row.tagId"
                          filterable
                          class="form-select form-control-sm w-full"
                          placeholder="选择企业标签"
                        >
                          <el-option
                            v-for="tag in enabledTags"
                            :key="tag.id"
                            :label="tag.tagName"
                            :value="tag.id"
                            :disabled="isTagUsed(tag.id, index)"
                          />
                        </el-select>
                      </td>
                      <td class="px-3 py-2.5 text-center">
                        <input v-model="row.enabled" type="checkbox" class="form-check">
                      </td>
                      <td class="px-3 py-2.5">
                        <input
                          v-model.number="row.priority"
                          type="number"
                          min="1"
                          step="1"
                          class="form-input form-control-sm w-full text-right tabular-nums"
                        >
                      </td>
                      <td class="data-table-sticky-right data-table-sticky-action px-3 py-2.5 text-right">
                        <button type="button" class="table-action-button is-danger" v-permission="'enterprise-invoice:plan-customer:manage'" title="删除" @click="removeRow(index)">
                          <TrashIcon class="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <button type="button" class="btn-secondary mt-3 inline-flex w-full items-center justify-center gap-1.5 sm:w-auto" v-permission="'enterprise-invoice:plan-customer:manage'" :disabled="rows.length >= enabledTags.length" @click="addRow">
              <PlusIcon class="h-4 w-4" />
              新增偏好
            </button>
          </main>

          <footer class="flex flex-col-reverse gap-3 border-t border-slate-200 px-4 py-4 sm:flex-row sm:justify-end sm:px-5">
            <button type="button" class="btn-secondary w-full sm:w-auto" :disabled="submitting" @click="close">取消</button>
            <button type="button" class="btn-primary w-full sm:w-auto" v-permission="'enterprise-invoice:plan-customer:manage'" :disabled="submitting" @click="submit">
              {{ submitting ? '保存中...' : '保存标签偏好' }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useWheelToHorizontalScroll } from '../composables/useWheelToHorizontalScroll'
import { preferencesForCustomer } from '../downstreamRules.mjs'
import { displayInvoiceCompanyName } from '../matrixRules.mjs'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  customer: { type: Object, default: null },
  tags: { type: Array, default: () => [] },
  preferences: { type: Array, default: () => [] },
  submitting: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'submit'])

const rows = ref([])
const tagPreferenceTableScrollRef = useWheelToHorizontalScroll()
let nextLocalId = 1

const companyName = computed(() => props.customer ? displayInvoiceCompanyName(props.customer) : '-')
const enabledTags = computed(() =>
  [...(props.tags || [])]
    .filter(tag => tag?.enabled !== false)
    .sort((left, right) => {
      const leftOrder = Number(left?.displayOrder || 0)
      const rightOrder = Number(right?.displayOrder || 0)
      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return String(left?.tagName || '').localeCompare(String(right?.tagName || ''), 'zh-CN')
    })
)

function resetRows() {
  const source = preferencesForCustomer(props.preferences, props.customer?.id)
  rows.value = source.map((item, index) => ({
    localId: nextLocalId++,
    tagId: item.tagId,
    priority: item.priority || index + 1,
    enabled: item.enabled !== false
  }))
}

function addRow() {
  const used = new Set(rows.value.map(row => String(row.tagId)).filter(Boolean))
  const first = enabledTags.value.find(tag => !used.has(String(tag.id)))
  rows.value.push({
    localId: nextLocalId++,
    tagId: first?.id || null,
    priority: rows.value.length + 1,
    enabled: true
  })
}

function removeRow(index) {
  rows.value.splice(index, 1)
}

function isTagUsed(tagId, ignoreIndex) {
  return rows.value.some((row, index) =>
    index !== ignoreIndex &&
    row.tagId != null &&
    String(row.tagId) === String(tagId)
  )
}

function close() {
  emit('update:modelValue', false)
}

function submit() {
  const normalized = []
  const seen = new Set()
  for (const [index, row] of rows.value.entries()) {
    if (!row.tagId) {
      ElMessage.warning(`第 ${index + 1} 行请选择企业标签`)
      return
    }
    if (seen.has(String(row.tagId))) {
      ElMessage.warning('偏好标签不能重复')
      return
    }
    seen.add(String(row.tagId))
    normalized.push({
      tagId: row.tagId,
      priority: Number(row.priority || index + 1),
      enabled: row.enabled !== false
    })
  }
  emit('submit', {
    customer: props.customer,
    preferences: normalized
  })
}

watch(() => props.modelValue, value => {
  if (value) resetRows()
})

watch(() => [props.customer?.id, props.preferences], () => {
  if (props.modelValue) resetRows()
}, { deep: true })
</script>
